"use client";

import React, { useState } from "react";
import { motion } from "motion/react";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  User,
  Building,
  ArrowRight,
  CheckCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { GlassCard } from "@/src/components/GlassCard";
import { signUp } from "@/src/actions/auth";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/src/components/ui/form";
import { Input } from "@/src/components/ui/input";
import { cn } from "@/src/components/ui/utils";
import { Button } from "@/src/components/ui/button";
import { requiredNoWhitespace } from "@/src/utils/validations";


const inputBaseClassName =
  "w-full h-auto pl-12 pr-4 py-3.5 rounded-[20px] backdrop-blur-xl bg-white/20 dark:bg-black/20 border text-walnut-900 dark:text-white placeholder-walnut-600 dark:placeholder-walnut-400 focus:outline-none focus:border-walnut-500 focus:ring-2 focus:ring-walnut-500/20 transition-all border-white/30 dark:border-white/10";
const inputWithToggleBaseClassName =
  "w-full h-auto pl-12 pr-12 py-3.5 rounded-[20px] backdrop-blur-xl bg-white/20 dark:bg-black/20 border text-walnut-900 dark:text-white placeholder-walnut-600 dark:placeholder-walnut-400 focus:outline-none focus:border-walnut-500 focus:ring-2 focus:ring-walnut-500/20 transition-all border-white/30 dark:border-white/10";
const labelClassName = "text-sm font-semibold text-walnut-900 dark:text-white";

type SignUpFormValues = {
  fullName: string;
  email: string;
  company: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
};

export const SignUpPage: React.FC = () => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = searchParams.get("next");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm<SignUpFormValues>({
    defaultValues: {
      fullName: "",
      email: "",
      company: "",
      password: "",
      confirmPassword: "",
      agreeToTerms: false,
    },
  });

  const onSubmit = async (values: SignUpFormValues) => {
    const password = values.password.trim();
    const confirmPassword = values.confirmPassword.trim();
    if (password !== confirmPassword) {
      form.setError("confirmPassword", {
        message: t("auth.signUp.errors.passwordMismatch"),
      });
      return;
    }
    if (password.length < 6) {
      form.setError("password", {
        message: t("auth.signUp.errors.passwordTooShort"),
      });
      return;
    }

    const result = await signUp({
      email: values.email.trim(),
      password,
      fullName: values.fullName.trim(),
      companyName: values.company.trim() || undefined,
    });

    if ("error" in result) {
      toast.error(result.error, { className: "bg-red-500" });
      return;
    }
    if ("emailExists" in result) {
      form.setError("email", {
        message: t("auth.signUp.errors.emailAlreadyUsed"),
      });
      return;
    }

    const validNext =
      next &&
      (next.startsWith("/dashboard") ||
        next.startsWith("/studio") ||
        next.startsWith("/diecuts"))
        ? next
        : "/dashboard";

    if (result.session) {
      router.push(validNext);
    } else {
      router.push(
        `/signin?registered=1${next ? `&next=${encodeURIComponent(next)}` : ""}`,
      );
    }
  };

  const benefits = [
    { icon: CheckCircle, text: t("auth.signUp.benefits.templates") },
    { icon: CheckCircle, text: t("auth.signUp.benefits.preview") },
    { icon: CheckCircle, text: t("auth.signUp.benefits.collaboration") },
    { icon: CheckCircle, text: t("auth.signUp.benefits.quotes") },
    { icon: CheckCircle, text: t("auth.signUp.benefits.support") },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
      <div className="max-w-6xl w-full grid lg:grid-cols-2 gap-8 items-center">
        {/* Left Column - Branding */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="hidden lg:block"
        >
          <div className="mb-8">
            <h1 className="text-5xl font-bold text-walnut-900 dark:text-white mb-4">
              {t("auth.signUp.title")}
            </h1>
            <p className="text-lg text-walnut-700 dark:text-walnut-300">
              {t("auth.signUp.subtitle")}
            </p>
          </div>

          <div className="space-y-4">
            {benefits.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                className="flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-walnut-500 to-walnut-700 flex items-center justify-center shrink-0">
                  <item.icon className="w-5 h-5 text-white" />
                </div>
                <span className="text-walnut-700 dark:text-walnut-300 font-medium">
                  {item.text}
                </span>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 p-6 rounded-[20px] backdrop-blur-[5px] bg-white/10 dark:bg-white/5 border border-white/20 dark:border-white/10">
            <p className="text-sm text-walnut-700 dark:text-walnut-300 italic">
              &quot;{t("auth.signUp.testimonial.quote")}&quot;
            </p>
            <div className="mt-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-walnut-400 to-walnut-600" />
              <div>
                <div className="font-semibold text-walnut-900 dark:text-white text-sm">
                  {t("auth.signUp.testimonial.author")}
                </div>
                <div className="text-xs text-walnut-600 dark:text-walnut-400">
                  {t("auth.signUp.testimonial.position")}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right Column - Sign Up Form */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <GlassCard className="p-8 md:p-12">
            <div className="lg:hidden mb-8 text-center">
              <h1 className="text-3xl font-bold text-walnut-900 dark:text-white mb-2">
                {t("auth.signUp.mobileTitle")}
              </h1>
              <p className="text-walnut-700 dark:text-walnut-300">
                {t("auth.signUp.mobileSubtitle")}
              </p>
            </div>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                  <FormField
                    control={form.control}
                    name="fullName"
                    rules={{
                      required: t("auth.signUp.errors.fullNameRequired"),
                      validate: requiredNoWhitespace(
                        t("auth.signUp.errors.fullNameRequired"),
                      ),
                    }}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className={labelClassName}>
                          {t("auth.signUp.fullName")}
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-walnut-600 dark:text-walnut-400 pointer-events-none" />
                            <Input
                              {...field}
                              type="text"
                              placeholder={t("auth.signUp.fullNamePlaceholder")}
                              className={cn(
                                inputBaseClassName,
                                fieldState?.error &&
                                  "border-destructive focus:border-destructive focus:ring-destructive/20",
                              )}
                              aria-invalid={!!fieldState?.error}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className={labelClassName}>
                          {t("auth.signUp.company")}{" "}
                          <span className="text-walnut-500 dark:text-walnut-400 font-normal">
                            {t("auth.signUp.companyOptional")}
                          </span>
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-walnut-600 dark:text-walnut-400 pointer-events-none" />
                            <Input
                              {...field}
                              type="text"
                              placeholder={t("auth.signUp.companyPlaceholder")}
                              className={cn(
                                inputBaseClassName,
                                fieldState?.error &&
                                  "border-destructive focus:border-destructive focus:ring-destructive/20",
                              )}
                              aria-invalid={!!fieldState?.error}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="email"
                  rules={{
                    required: t("auth.signUp.errors.emailRequired"),
                    validate: requiredNoWhitespace(
                      t("auth.signUp.errors.emailRequired"),
                    ),
                  }}
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel className={labelClassName}>
                        {t("auth.signUp.email")}
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-walnut-600 dark:text-walnut-400 pointer-events-none" />
                          <Input
                            {...field}
                            type="email"
                            placeholder={t("auth.signUp.emailPlaceholder")}
                            className={cn(
                              inputBaseClassName,
                              fieldState?.error &&
                                "border-destructive focus:border-destructive focus:ring-destructive/20",
                            )}
                            aria-invalid={!!fieldState?.error}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                  <FormField
                    control={form.control}
                    name="password"
                    rules={{
                      required: t("auth.signUp.errors.passwordRequired"),
                      validate: (value) => {
                        if (typeof value !== "string") return true;
                        const trimmed = value.trim();
                        if (trimmed.length === 0)
                          return t("auth.signUp.errors.passwordRequired");
                        if (trimmed.length < 6)
                          return t("auth.signUp.errors.passwordTooShort");
                        return true;
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className={labelClassName}>
                          {t("auth.signUp.password")}
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-walnut-600 dark:text-walnut-400 pointer-events-none" />
                            <Input
                              {...field}
                              type={showPassword ? "text" : "password"}
                              placeholder={t("auth.signUp.passwordPlaceholder")}
                              className={cn(
                                inputWithToggleBaseClassName,
                                fieldState?.error &&
                                  "border-destructive focus:border-destructive focus:ring-destructive/20",
                              )}
                              aria-invalid={!!fieldState?.error}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-4 top-1/2 -translate-y-1/2 text-walnut-600 dark:text-walnut-400 hover:text-walnut-800 dark:hover:text-walnut-200 transition-colors"
                            >
                              {showPassword ? (
                                <EyeOff className="w-5 h-5" />
                              ) : (
                                <Eye className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    rules={{
                      required: t("auth.signUp.errors.confirmPasswordRequired"),
                      validate: requiredNoWhitespace(
                        t("auth.signUp.errors.confirmPasswordRequired"),
                      ),
                    }}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className={labelClassName}>
                          {t("auth.signUp.confirmPassword")}
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-walnut-600 dark:text-walnut-400 pointer-events-none" />
                            <Input
                              {...field}
                              type={showConfirmPassword ? "text" : "password"}
                              placeholder={t(
                                "auth.signUp.confirmPasswordPlaceholder",
                              )}
                              className={cn(
                                inputWithToggleBaseClassName,
                                fieldState?.error &&
                                  "border-destructive focus:border-destructive focus:ring-destructive/20",
                              )}
                              aria-invalid={!!fieldState?.error}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              className="absolute right-4 top-1/2 -translate-y-1/2 text-walnut-600 dark:text-walnut-400 hover:text-walnut-800 dark:hover:text-walnut-200 transition-colors"
                            >
                              {showConfirmPassword ? (
                                <EyeOff className="w-5 h-5" />
                              ) : (
                                <Eye className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="agreeToTerms"
                  rules={{
                    required: t("auth.signUp.errors.agreeToTermsRequired"),
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            ref={field.ref}
                            className="w-5 h-5 mt-0.5 rounded-lg border-2 border-white/30 dark:border-white/10 bg-white/20 dark:bg-black/20 checked:bg-gradient-to-br checked:from-walnut-600 checked:to-walnut-700 checked:border-walnut-600 focus:outline-none focus:ring-2 focus:ring-walnut-500/20 transition-all cursor-pointer shrink-0"
                          />
                          <span className="text-sm text-walnut-700 dark:text-walnut-300">
                            {t("auth.signUp.agreeToTerms")}{" "}
                            <Link
                              href="/terms"
                              className="text-walnut-600 dark:text-walnut-400 hover:text-walnut-800 dark:hover:text-walnut-200 font-medium transition-colors"
                            >
                              {t("auth.signUp.termsOfService")}
                            </Link>{" "}
                            {t("auth.signUp.and")}{" "}
                            <Link
                              href="/privacy"
                              className="text-walnut-600 dark:text-walnut-400 hover:text-walnut-800 dark:hover:text-walnut-200 font-medium transition-colors"
                            >
                              {t("auth.signUp.privacyPolicy")}
                            </Link>
                          </span>
                        </label>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  variant="gradient"
                  size="lg"
                  pill
                  loading={form.formState.isSubmitting}
                  className="w-full"
                >
                  {t("auth.signUp.createAccountButton")}
                  <ArrowRight className="w-5 h-5" />
                </Button>
                <div className="text-center">
                  <span className="text-walnut-700 dark:text-walnut-300">
                    {t("auth.signUp.haveAccount")}{" "}
                  </span>
                  <Link
                    href={`/signin${next ? `?next=${encodeURIComponent(next)}` : ""}`}
                    className="text-walnut-600 dark:text-walnut-400 hover:text-walnut-800 dark:hover:text-walnut-200 font-semibold transition-colors"
                  >
                    {t("auth.signUp.signInLink")}
                  </Link>
                </div>
              </form>
            </Form>
          </GlassCard>
        </motion.div>
      </div>
    </div>
  );
};
